<template>
  <section class="offer-list" :class="{ 'offer-tile': viewType == 'tiles' }">
    <div class="container">
      <div v-if="!loaded" class="offers-list__loader">
        <Loader />
      </div>
      <div v-if="loaded && viewType !== 'map'" class="listingWrapper row">
        <Offer v-for="offer in offers" :key="offer.id" :offer="offer" :priceType="priceType" />
        <div class="offers-list__more" v-if="numberOfOffersToShow < allOffers.length" @click="showMoreOffers">
          Pokaż więcej ofert
        </div>
      </div>
      <ListingMap v-if="mapConfigLoaded && viewType === 'map'" :mapConfig="mapConfig" :offers="offers" :priceType="priceType" />
    </div>
  </section>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'

import ListingMap from '@/components/googleMap/ListingMap'
import Loader from '@/components/Loader'
import Offer from '@/components/offers-list/Offer'
import { offersListUpdated, offersListFilterChanged } from '@/events'
import { API } from '@/modules/api'
import { DataLayerEvent, pushEvent } from '@/modules/datalayer/helpers'
import ExternalDataSearchAdapter from '@/modules/search/ExternalDataAdapter'
import GoogleMapsService from '@/services/GoogleMapsService'
import MediaDetector from '@/utils/MediaDetector'

@Component({
  mixins: [
    MediaDetector,
    ExternalDataSearchAdapter,
  ],
  components: {
    Loader,
    Offer,
    ListingMap,
  },
  props: {
    initialUrlForLoadingData: {
      type: String,
      required: true,
    },
    shouldSetListingFilters: {
      type: Boolean,
      required: false,
    },
    initialViewType: {
      type: String,
      required: true,
    },
    initialPriceType: {
      type: String,
      required: true,
    },
    initialDirection: {
      type: String,
      required: true,
    },
    initialOrderBy: {
      type: String,
      required: true,
    },
  },
})
export default class OffersList extends Vue {
  loaded = false
  mapConfigLoaded = false
  allOffers = []
  numberOfOffersToShow = 25
  mapConfig = {}

  get offers () {
    return this.allOffers
      .sort(this.compareOffers)
      .take(this.numberOfOffersToShow)
  }

  mounted () {
    this.loadOffers(this.initialUrlForLoadingData)
  }

  loadOffers (filterUrl) {
    offersListUpdated({ offers: this.allOffers, offersCount: -1, component: this })
    new API()
      .getOffersList(filterUrl)
      .then(response => { this.allOffers = response.offers })
      .then(() => { this.numberOfOffersToShow = this.allOffers.length })
      .then(() => { this.loaded = true })
      .then(() => { offersListUpdated({ offers: this.allOffers, offersCount: this.allOffers.length, component: this }) })
      .then(() => {
        /* eslint-disable */
        const eventItems =
          this.allOffers && this.allOffers.length
            ? this.allOffers.map((item, idx) => ({
              item_name: item['Hotel_Name'],
              item_id: item['Hotel_Id'],
              price: item['Merlin_AdultPrice'],
              item_category: item['Hotel_TextCategory'],
              index: idx,
              hotel_type: item['Merlin_BoardStandardDesc'],
              hotel_id: item['Merlin_HotelCode'],
              departure_date: item['Merlin_ParsedStartFullDate'],
              duration: item['Merlin_Duration'],
              hotel_standard: item['Hotel_Local_Standard_Stars_Css'],
              offer_type: item['Merlin_OfferTypeSitecore'] ? item['Merlin_OfferTypeSitecore'].Name : null,
            }))
            : []
        const [ event ] = DataLayerEvent.itemList('view_item_list', eventItems)
        event.ecommerce.item_list_name = window.location.origin + window.location.pathname
        pushEvent(event)
        /* eslint-enable */
      })

    offersListFilterChanged.subscribe(this.updateFilterViewAndSort)

    /**
     * IG-1196 Need to check if page is not in MOBILE mode because now we have two different
     * implementations of search component - old (TAB & WEB) and new (MOB).
     * MOB version will run with dataset initialized in 'MobileListing.vue' component.
     * Reason - different architecture (MOB version is the target which we will base on).
     */
    if (this.shouldSetListingFilters && !this.isMobile) this.updateSearchFilters()
  }

  clearOffers () {
    this.allOffers = []
  }

  initMap () {
    new GoogleMapsService().init()
      .then(() => new API().getJsonForListingMap())
      .then(response => { this.mapConfig = response })
      .then(() => { this.mapConfigLoaded = true })
  }

  showMoreOffers () {
    if (this.numberOfOffersToShow + 25 < this.allOffers.length) {
      this.numberOfOffersToShow += 25
    } else {
      this.numberOfOffersToShow = this.allOffers.length
    }
  }

  orderBy = this.initialOrderBy
  direction = this.initialDirection
  priceType = this.initialPriceType
  viewType = this.initialViewType

  updateFilterViewAndSort (e, { orderBy, direction, priceType, viewType } = e.detail) {
    log.debug('updateFilterViewAndSort', 'orderBy', orderBy, 'direction', direction, 'priceType', priceType, 'viewType', viewType)

    this.orderBy = orderBy
    this.direction = direction
    this.priceType = priceType
    this.viewType = viewType
    
    if (this.viewType === 'map' && !this.mapConfigLoaded) this.initMap()
  }

  compareOffers (o1, o2) {
    const orderToFieldMap = { 
      'price': this.priceType == 'person' ? 'Merlin_AdultPrice_V' : 'Merlin_FullPriceParsed_V',
      'name': 'Hotel_Name',
      'standard': 'hotelStandard',
    }

    let v1 = null
    let v2 = null
    if (this.direction == 'asc') {
      v1 = o1[orderToFieldMap[this.orderBy]]
      v2 = o2[orderToFieldMap[this.orderBy]]
    } else {
      v1 = o2[orderToFieldMap[this.orderBy]]
      v2 = o1[orderToFieldMap[this.orderBy]]
    }

    return v1.compareTo(v2)
  }
}
</script>

<style lang="scss">
.offer-list {
  margin-top: 35px;

  @include media(tablet) {
    margin-top: 0;
  }

  .row {
    margin-left: 0;
    margin-right: 0;
  }

  .listingWrapper {
    display: grid;
    grid-template-columns: 1fr;

    &::before {
      display: none;
    }
  }
  
  @media only screen and (min-width: 1350px){
  .offer-list-item__image {
    width: 380px !important;
  }

  .offer-list-item__price-promo {
    margin-bottom: -7px;
    position: absolute;
    top: -46px;
  }
}

  &.offer-tile {
    .listingWrapper {
      @include media(desktop) {
        grid-template-columns: repeat(2, 1fr);
        grid-column-gap: 15px;

        .offer-list-item {
          &__hotel-info {
            display: none;
          }
          &__image {
            width: 270px;
          }
        }
      }
    }

    .offer-list-item__price {
      margin-top: 0;
    }
    .offer-list-item__brands {
      width: 132px;
    }
    .offer-list-item__brand {
      width: calc(100% / 3);

      img {
        width: 100%;
      }
    }
  }
}

.listing-options__result-info {
  position: absolute;
  bottom: -55px;

  @include media(tablet) {
    bottom: initial;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/OffersList.vue